/* V9: independent horizontal product layout and optional, fail-open entry effects. */
(() => {
  const home = document.body.classList.contains('vnk-polished-home');
  const instances = new WeakMap();
  const seen = new WeakSet();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Paused Web Animations hold only prepared elements. CSS itself stays visible.
  const animations = new WeakMap();
  const accents = new WeakMap();
  const compact = window.matchMedia('(max-width:749px)');
  const pending = new Set();
  const active = new Set();
  window.vnkRevealRescue = () => {
    [...active].forEach(cancelEffect);
    active.clear(); pending.clear();
  };
  const groupTimes = new WeakMap();
  const waitingImages = new WeakSet();
  const readyImages = new WeakSet();
  const revealSelector = [
    '.vnk-polished-home [data-vnk-reveal]',
    '.vnk-polished-home .vnk-journey-steps li',
    '.vnk-polished-home .article',
    '.vnk-polished-home .section-columns .column__inner',
    '.vnk-polished-home .announcement__text',
    '.vnk-polished-home .vnk-brand-logos__heading',
    '.vnk-polished-home .grid__heading',
    '.vnk-polished-home .vnk-load-more-wrapper',
    '.vnk-polished-home .blog__title',
    '.vnk-polished-home .accordion__heading',
    '.vnk-polished-home .faq .accordion',
    '.vnk-polished-collection .collection__nav',
    '.vnk-polished-collection .search-outer',
    '.supporting-menu .supporting-menu__item',
    '.vnk-polished-collection .collection__sidebar',
    '.vnk-polished-collection .collection__sidebar__slider',
    '.vnk-polished-collection .collection__sidebar__slide-out',
    '.vnk-polished-collection .filter-group',
    '.vnk-polished-collection .collection__active__filters',
    '.vnk-polished-collection .collection__sidebar__buttons',
    '.vnk-polished-collection #MainContent [data-aos]',
    '.vnk-polished-collection .pagination',
    ".template-product #MainContent .index-product .product__slide",
    ".template-product #MainContent .index-product .form__width > .product__block",
    '.template-product #MainContent .index-product .product-tabs',
    '.template-product .vnk-info-accordion',
    '.template-product .vnk-founder-portrait',
    '.vnk-editorial-page .hero__title',
    '.vnk-editorial-page .hero__rte',
    '.vnk-editorial-page .column__inner',
    '.vnk-editorial-page .faq .accordion',
    '.vnk-editorial-page .accordion__heading',
    '.vnk-polished-collection .subcategory-nav a',
    'body .product-item:not(.predictive-search__grid-item):not(.drawer--cart .product-item)',
    '.header-sections .header__desktop [data-top-link] .navtext',
    '.header-sections .header__mobile__nav .navtext',
    '.vnk-footer-area .footer__block:not(.footer__block--divider)',
    '.vnk-footer-area .vnk-footer-social-row'
  ].join(', ');
  function viewportSize() {
    return {
      height: window.visualViewport?.height || window.innerHeight,
      width: window.visualViewport?.width || window.innerWidth,
      top: window.visualViewport?.offsetTop || 0,
      left: window.visualViewport?.offsetLeft || 0
    };
  }
  function inView(element) {
    const r = element.getBoundingClientRect();
    const viewport = viewportSize();
    const vertical = Math.min(r.bottom, viewport.top + viewport.height) - Math.max(r.top, viewport.top);
    const horizontal = Math.min(r.right, viewport.left + viewport.width) - Math.max(r.left, viewport.left);
    return r.width > 0 && r.height > 0 &&
      vertical >= Math.min(r.height * .18, 72, viewport.height * .12) &&
      horizontal >= Math.min(r.width * .18, 48);
  }
  function cancelEffect(element) {
    animations.get(element)?.cancel();
    accents.get(element)?.cancel();
  }
  function iconEntry(element, delay) {
    const icon = element.matches('.column__inner') ? element.querySelector('.icon__animated') : null;
    if (!icon || typeof icon.animate !== 'function') return null;
    try {
      const effect = icon.animate([
        { transform: 'translate3d(0, 6px, 0)', opacity: 0 },
        { transform: 'translate3d(0, 0, 0)', opacity: 1 }
      ], { duration: compact.matches ? 650 : 1800, delay: delay + (compact.matches ? 0 : 100), easing: 'cubic-bezier(.25,.1,.25,1)', fill: 'backwards' });
      accents.set(element, effect);
      return effect;
    } catch (_) { return null; }
  }
  function show(element) {
    pending.delete(element);
    const animation = animations.get(element);
    if (!animation) return;
    if (motion.matches) { cancelEffect(element); active.delete(element); return; }
    // Mobile brand cards enter after their lazy image is available, not before it loads.
    if ((compact.matches && element.matches('.vnk-grid-card')) || element.matches('.vnk-founder-portrait')) {
      const image = element.matches('.vnk-founder-portrait') ? element : element.querySelector('img');
      if (image && !image.complete && !readyImages.has(image)) {
        pending.add(element);
        if (!waitingImages.has(image)) {
          waitingImages.add(image);
          let timer;
          const ready = () => {
            clearTimeout(timer);
            image.removeEventListener('load', ready);
            image.removeEventListener('error', ready);
            readyImages.add(image);
            scheduleScan();
          };
          image.addEventListener('load', ready, { once:true });
          image.addEventListener('error', ready, { once:true });
          timer = setTimeout(ready, 1800); // Fail open on stalled image requests.
        }
        return;
      }
    }
    const collectionEntry = element.closest('.vnk-polished-collection #MainContent');
    const productEntry = element.closest('.template-product #MainContent .index-product');
    const brandGroup = element.matches('.vnk-grid-card, .vnk-load-more-wrapper') ? element.closest('.vnk-grid-container') : null;
    const shopGroup = element.matches('.vnk-shop-reveal') ? element.closest('.vnk-shop-track') : null;
    const group = compact.matches ? document.body : brandGroup || shopGroup || collectionEntry || productEntry || element.closest('.subcategory-nav, #CollectionLoop, .header__menu, .header__mobile__nav__scroller, .vnk-grid-wrapper, .vnk-shop-track, .grid, .vnk-journey-steps, .vnk-footer-grid, .vnk-brand-logos__track, .shopify-section') || element.parentElement || element;
    const now = performance.now();
    const maxWait = compact.matches ? (brandGroup ? 600 : 120) : brandGroup ? 1440 : shopGroup ? 540 : collectionEntry ? 240 : productEntry ? 450 : 900;
    // Mobile uses one ordered timeline across sections; no capped delays that merge later entries.
    const start = compact.matches ? Math.max(now, groupTimes.get(group) || now) : Math.min(now + maxWait, Math.max(now, groupTimes.get(group) || now + (collectionEntry || productEntry ? 40 : 0)));
    const collectionItem = element.matches('.subcategory-nav a, .product-item');
    const iconGroup = element.matches('.column__inner') && element.closest('[id^="SectionColumns--"][id$="__icons"]');
    const stagger = compact.matches ? (brandGroup ? 150 : 120) : iconGroup ? 420 : shopGroup ? 180 : collectionEntry ? 40 : productEntry ? 75 : element.matches('.vnk-grid-card') ? 120 : collectionItem ? 90 : 110;
    groupTimes.set(group, start + stagger);
    try {
      animation.effect.updateTiming({ delay: start - now });
      animation.play();
      const accent = iconEntry(element, start - now);
      Promise.all([animation.finished, accent?.finished]).then(() => active.delete(element), () => active.delete(element));
    } catch (_) { cancelEffect(element); active.delete(element); }
    revealObserver?.unobserve(element);
  }
  function scan() {
    // Read all geometry before starting animations; avoid interleaved layout reads/writes.
    [...pending].filter(inView)
      .sort((a, b) => a.compareDocumentPosition(b) & 2 ? 1 : -1)
      .forEach(show);
  }
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    if (compact.matches) { scheduleScan(); return; }
    entries.filter(entry => entry.isIntersecting && pending.has(entry.target) && inView(entry.target))
      .sort((a, b) => a.target.compareDocumentPosition(b.target) & 2 ? 1 : -1)
      .forEach(entry => show(entry.target));
  }, { threshold: [0, .12, .2, .4], rootMargin: '0px' }) : null;
  function prepare(element) {
    if (seen.has(element)) return;
    seen.add(element);
    if (motion.matches || typeof element.animate !== 'function') { element.dataset.vnkRevealPrepared = ''; return; }
    // The theme's AOS must not animate the same element again.
    if (element.hasAttribute('data-aos')) {
      element.dataset.vnkNativeAos = element.getAttribute('data-aos');
      element.removeAttribute('data-aos');
    }
    if (compact.matches || element.matches('.product__slide, .product__block')) {
      element.querySelectorAll('[data-aos]').forEach(child => {
        child.dataset.vnkNativeAos = child.getAttribute('data-aos');
        child.removeAttribute('data-aos');
      });
    }
    // Essential mobile controls and gallery slides must be available immediately.
    // Native gallery transforms and drawer focus management remain authoritative.
    if (compact.matches && element.matches('.product__slide, .product__block, .collection__nav, .collection__sidebar, .collection__sidebar__slider, .collection__sidebar__slide-out, .filter-group, .collection__sidebar__buttons, .subcategory-nav a, .navtext')) {
      element.dataset.vnkRevealPrepared = '';
      return;
    }
    let animation;
    try {
      const opacityOnly = element.matches('.vnk-founder-portrait, .product__slide, .product__block, .collection__nav, .collection__sidebar, .collection__sidebar__slider, .collection__sidebar__slide-out, .product-tabs');
      animation = element.animate(opacityOnly ? [{ opacity: 0 }, { opacity: 1 }] : [
        { opacity: 0, transform: compact.matches ? 'translate3d(0, 8px, 0)' : 'translate3d(0, 12px, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ], { duration: compact.matches ? (element.matches('.vnk-grid-card') ? 780 : 650) : element.matches('.column__inner') && element.closest('[id^="SectionColumns--"][id$="__icons"]') ? 1500 : element.closest('.vnk-polished-collection #MainContent') ? 600 : element.matches('.product__slide, .product__block') ? 700 : element.matches('.vnk-grid-card, .vnk-load-more-wrapper') ? 950 : element.matches('.subcategory-nav a, .product-item') ? 900 : 1100, easing: 'cubic-bezier(.25,.1,.25,1)', fill: 'backwards' });
      animation.pause();
      animation.currentTime = 0;
      animations.set(element, animation);
      pending.add(element);
      active.add(element);
      revealObserver?.observe(element);
    } catch (_) {
      animation?.cancel();
      pending.delete(element);
      active.delete(element);
    } finally {
      element.dataset.vnkRevealPrepared = '';
    }
  }
  function observeReveals(root) {
    const candidates = [...(root.matches?.(revealSelector) ? [root] : []), ...root.querySelectorAll(revealSelector)];
    const parents = new Set(candidates);
    candidates.forEach(element => {
      let parent = element.parentElement;
      while (parent) {
        if (!element.matches('.vnk-founder-portrait') && (parents.has(parent) || seen.has(parent)) && !parent.matches('.collection__nav, .collection__sidebar__slider, .collection__sidebar__slide-out')) {
          if (element.hasAttribute('data-aos')) { element.dataset.vnkNativeAos = element.getAttribute('data-aos'); element.removeAttribute('data-aos'); }
          element.dataset.vnkRevealPrepared = ''; return;
        }
        parent = parent.parentElement;
      }
      prepare(element);
    });
  }
  let scheduled = false;
  const scheduleScan = () => {
    if (scheduled || !pending.size) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  };
  // Passive fallback also handles native horizontal scrolling and anchor jumps.
  document.addEventListener('scroll', scheduleScan, { passive: true, capture: true });
  window.addEventListener('resize', scheduleScan, { passive: true });
  window.addEventListener('pageshow', scheduleScan);
  window.visualViewport?.addEventListener('resize', scheduleScan, { passive: true });
  document.addEventListener('visibilitychange', scheduleScan);
  document.addEventListener('focusin', event => {
    [...active].filter(element => element === event.target || element.contains(event.target))
      .forEach(element => { cancelEffect(element); pending.delete(element); active.delete(element); revealObserver?.unobserve(element); });
  });
  motion.addEventListener('change', () => {
    if (!motion.matches) return;
    active.forEach(element => { cancelEffect(element); revealObserver?.unobserve(element); });
    active.clear(); pending.clear();
  });

  function init(root = document) {
    observeReveals(root);
    if (!home) { scan(); if (root === document) window.clearTimeout?.(window.vnkRevealFallback); return; }
    root.querySelectorAll('.vnk-banner').forEach(banner => {
      if (!motion.matches && !banner.hasAttribute('data-vnk-enter')) banner.dataset.vnkEnter = '';
    });

    root.querySelectorAll('.vnk-grid-container').forEach(grid => {
      if (instances.has(grid)) return;
      const button = grid.querySelector('[data-load-more]');
      const cards = [...grid.querySelectorAll('.vnk-grid-item')];
      if (!button) return;
      const controller = new AbortController();
      instances.set(grid, controller);
      const desktop = window.matchMedia('(min-width: 1025px)');
      let visible = desktop.matches ? 12 : 6;
      const update = () => {
        cards.forEach((card, i) => card.classList.toggle('hidden', i >= visible));
        button.classList.toggle('hidden', visible >= cards.length);
      };
      const revealBatch = batch => batch.forEach(card => {
        const layer = card.querySelector('.vnk-grid-card');
        if (layer) prepare(layer);
      });
      const keepLazy = batch => batch.forEach(card => {
        const image = card.querySelector('img');
        if (image) image.loading = 'lazy';
      });
      // Effects and visibility changes run synchronously in one task, before the next paint.
      revealBatch(cards.slice(0, visible));
      keepLazy(cards.slice(0, visible));
      update();
      grid.dataset.vnkReady = '';
      button.addEventListener('click', () => {
        const next = cards[visible];
        const buttonHadFocus = document.activeElement === button;
        const start = visible;
        visible += 6;
        keepLazy(cards.slice(start, visible));
        revealBatch(cards.slice(start, visible));
        update();
        scheduleScan();
        if (button.classList.contains('hidden') && next?.matches('a') && buttonHadFocus) next.focus({ preventScroll: true });
      }, { signal: controller.signal });
      window.addEventListener('resize', () => {
        if (!desktop.matches || visible >= 12) return;
        const start = visible;
        visible = 12;
        keepLazy(cards.slice(start, visible));
        revealBatch(cards.slice(start, visible));
        update();
        scheduleScan();
      }, { signal: controller.signal });
    });

    root.querySelectorAll('.vnk-shop').forEach(carousel => {
      if (instances.has(carousel)) return;
      const viewport = carousel.querySelector('.vnk-shop-viewport');
      const track = carousel.querySelector('.vnk-shop-track');
      const prev = carousel.querySelector('.vnk-shop-prev');
      const next = carousel.querySelector('.vnk-shop-next');
      if (!viewport || !track || !prev || !next) return;
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const rtl = getComputedStyle(viewport).direction === 'rtl';
      let autoStopped = false;
      let autoTimer = null;
      const update = () => {
        const offset = Math.abs(viewport.scrollLeft);
        const max = viewport.scrollWidth - viewport.clientWidth;
        prev.disabled = max <= 2 || (autoStopped && offset <= 2);
        next.disabled = max <= 2 || (autoStopped && offset >= max - 2);
        prev.setAttribute('aria-disabled', String(prev.disabled));
        next.setAttribute('aria-disabled', String(next.disabled));
      };
      const move = direction => {
        const card = track.firstElementChild;
        if (!card) return;
        const step = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0);
        viewport.scrollBy({ left: step * direction * (rtl ? -1 : 1), behavior: motion.matches ? 'instant' : 'smooth' });
      };
      const stopAuto = () => {
        autoStopped = true;
        if (autoTimer !== null) clearInterval(autoTimer);
        autoTimer = null;
        carousel.dataset.vnkAutoStopped = '';
        update();
      };
      const advance = () => {
        if (autoStopped || document.hidden || motion.matches || !inView(viewport)) return;
        if ((!compact.matches && carousel.matches(':hover')) || carousel.contains(document.activeElement)) return;
        if ([...active].some(element => element.matches('.vnk-shop-reveal') && carousel.contains(element) && inView(element))) return;
        const max = viewport.scrollWidth - viewport.clientWidth;
        if (max <= 2) return;
        if (Math.abs(viewport.scrollLeft) >= max - 2) viewport.scrollTo({ left: 0, behavior: 'smooth' });
        else move(1);
      };
      prev.addEventListener('click', () => { stopAuto(); move(-1); }, options);
      next.addEventListener('click', () => { stopAuto(); move(1); }, options);
      autoTimer = setInterval(advance, 3000);
      viewport.addEventListener('scroll', update, { ...options, passive: true });
      viewport.addEventListener('pointerdown', stopAuto, { ...options, passive: true });
      viewport.addEventListener('wheel', stopAuto, { ...options, passive: true });
      viewport.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        stopAuto();
        move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      }, options);
      const observer = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
      observer?.observe(viewport);
      window.addEventListener('resize', update, options);
      instances.set(carousel, { abort: () => { controller.abort(); observer?.disconnect(); if (autoTimer !== null) clearInterval(autoTimer); } });
      update();
      carousel.dataset.vnkReady = '';
    });

    root.querySelectorAll('.vnk-brand-logos').forEach(press => {
      if (instances.has(press)) return;
      const track = press.querySelector('.vnk-brand-logos__track');
      const copy = track?.querySelector('[data-vnk-logo-copy]');
      if (!track || !copy || !track.firstElementChild) return;
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const measure = () => {
        if (motion.matches) return;
        track.dataset.vnkDistance = '';
        const distance = Math.abs(copy.offsetLeft - track.firstElementChild.offsetLeft);
        if (distance > 0) track.style.setProperty('--vnk-logo-distance', `${distance}px`);
      };
      const observer = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
      observer?.observe(press);
      window.addEventListener('resize', measure, options);
      motion.addEventListener('change', measure, options);
      instances.set(press, { abort: () => { controller.abort(); observer?.disconnect(); } });
      measure();
      press.dataset.vnkReady = '';
    });

    root.querySelectorAll('.vnk-shop-card').forEach(card => {
      if (instances.has(card)) return;
      const image = card.querySelector('[data-vnk-src]');
      if (!image) return;
      const controller = new AbortController();
      instances.set(card, controller);
      const load = () => {
        if (image.dataset.vnkLoaded) return;
        image.dataset.vnkLoaded = 'true';
        const ready = () => {
          if (image.complete && image.naturalWidth > 0 && !image.currentSrc?.startsWith('data:')) card.dataset.vnkAltReady = '';
        };
        image.addEventListener('load', ready, { signal: controller.signal });
        image.srcset = image.dataset.vnkSrcset || '';
        image.src = image.dataset.vnkSrc;
        ready();
      };
      card.addEventListener('pointerenter', () => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) load();
      }, { signal: controller.signal });
      card.addEventListener('focusin', load, { signal: controller.signal });
    });

    root.querySelectorAll('.vnk-newsletter-editorial form').forEach(form => {
      if (instances.has(form)) return;
      const button = form.querySelector('[data-vnk-submit-label]');
      if (!button) return; // Success animation is server-confirmed Liquid output.
      const controller = new AbortController();
      instances.set(form, controller);
      form.addEventListener('submit', event => {
        if (event.defaultPrevented || !form.checkValidity()) return;
        button.setAttribute('aria-busy', 'true');
        const text = button.querySelector('[data-vnk-submit-text]');
        if (text) text.textContent = button.dataset.vnkSubmitLabel;
      }, { signal: controller.signal });
    });
    scan();
    if (root === document) window.clearTimeout?.(window.vnkRevealFallback);
  }

  if ('MutationObserver' in window) {
    const main = document.querySelector('#MainContent');
    if (main) {
      let refreshQueued = false, removed = false;
      const addedRoots = new Set();
      const refresh = new MutationObserver(records => {
        for (const record of records) {
          for (const node of record.addedNodes) if (node.nodeType === 1) addedRoots.add(node);
          if ([...record.removedNodes].some(node => node.nodeType === 1)) removed = true;
        }
        if (refreshQueued || (!addedRoots.size && !removed)) return;
        refreshQueued = true;
        requestAnimationFrame(() => {
          refreshQueued = false;
          if (removed) {
            [...active].filter(element => !element.isConnected).forEach(element => {
              cancelEffect(element); pending.delete(element); active.delete(element); revealObserver?.unobserve(element);
            });
            groupTimes.delete(main); removed = false;
          }
          const roots = [...addedRoots].filter(element => element.isConnected);
          addedRoots.clear();
          roots.filter(element => !roots.some(other => other !== element && other.contains(element))).forEach(observeReveals);
          scheduleScan();
        });
      });
      refresh.observe(main, { childList: true, subtree: true });
    }
  }
  if ((document.body.classList.contains('vnk-polished-collection') || document.body.classList.contains('template-product') || document.body.classList.contains('vnk-editorial-page')) && ['loading','interactive'].includes(document.readyState)) {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.vnk-grid-container, .vnk-shop, .vnk-brand-logos, .vnk-shop-card, .vnk-newsletter-editorial form').forEach(element => {
      instances.get(element)?.abort();
      instances.delete(element);
    });
    event.target.querySelectorAll(`${revealSelector}, .vnk-grid-card`).forEach(element => {
      revealObserver?.unobserve(element);
      cancelEffect(element);
      seen.delete(element);
      pending.delete(element);
      active.delete(element);
    });
  });
})();


